import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Plus, Search, Edit, Trash2, ChevronDown, Check } from 'lucide-react';
import { greenBeanService } from '../../services/greenBeanService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ConfirmationDialog } from '../../components/common/ConfirmationDialog';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';

export const GreenBeanProfileList: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('inventory', 'write');
  const canDelete = can('inventory', 'delete');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemNames, setSelectedItemNames] = useState<string[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: profiles = [], isLoading, error } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
  });

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        await greenBeanService.delete(deleteId);
        queryClient.invalidateQueries({ queryKey: ['green-beans'] });
      } catch (err) {
        console.error('Failed to delete profile:', err);
        alert(t('greenBeanProfile.errors.deleteFailed'));
      } finally {
        setDeleteId(null);
      }
    }
  };

  const uniqueItemNames = useMemo(() => {
    return Array.from(
      new Set(
        profiles
          .map((profile) => profile.item_name)
          .filter((name): name is string => Boolean(name && name.trim()))
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [profiles]);

  const filteredProfiles = profiles.filter((profile) => {
    const matchesSearch =
      (profile.lot_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.origin_details || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.country || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.farm_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.profile_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (profile.profile_name || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesItem =
      selectedItemNames.length === 0 ||
      (profile.item_name ? selectedItemNames.includes(profile.item_name) : false);

    return matchesSearch && matchesItem;
  });

  if (isLoading) return <LoadingSpinner text={t('greenBeanProfile.loading')} />;

  if (error) return <div className="text-red-500">{t('greenBeanProfile.errors.loadFailed')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('greenBeanProfile.title')}</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/purchasing/green-beans/new')}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-test-id="green-bean-new-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('greenBeanProfile.actions.new')}
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex-1">
          <label htmlFor="greenBeanSearch" className="block text-sm font-medium text-gray-700">
            {t('greenBeanProfile.search')}
          </label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="greenBeanSearch"
              type="text"
              placeholder={t('greenBeanProfile.searchPlaceholder')}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm border p-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-test-id="green-bean-search-input"
            />
          </div>
        </div>
        <div className="w-full sm:w-72">
          <label className="block text-sm font-medium text-gray-700">{t('greenBeanProfile.itemName')}</label>
          <Listbox value={selectedItemNames} onChange={setSelectedItemNames} multiple>
            <div className="relative mt-1">
              <ListboxButton
                data-test-id="green-bean-item-filter"
                className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
              >
                <span className="block truncate">
                  {selectedItemNames.length === 0
                    ? t('greenBeanProfile.allItems')
                    : selectedItemNames.length === 1
                      ? selectedItemNames[0]
                      : t('greenBeanProfile.selectedCount', { count: selectedItemNames.length })}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
              <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {uniqueItemNames.map((itemName) => (
                  <ListboxOption
                    key={itemName}
                    value={itemName}
                    data-test-id={`green-bean-item-option-${itemName.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ focus }: { focus: boolean }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'
                      }`
                    }
                  >
                    {({ selected }: { selected: boolean }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {itemName}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                            <Check className="h-4 w-4" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                ))}
                {uniqueItemNames.length === 0 && (
                  <div className="py-2 px-4 text-gray-500 italic">{t('greenBeanProfile.emptyItems')}</div>
                )}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredProfiles.length === 0 ? (
            <li className="px-6 py-4 text-center text-gray-500">
              {t('greenBeanProfile.emptyProfiles')}
            </li>
          ) : (
            filteredProfiles.map((profile) => (
              <li key={profile.id} className="hover:bg-gray-50">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-primary-600 truncate">
                           { profile.profile_code || t('greenBeanProfile.values.noCode')} -
                        </p>
                        <p className="text-sm font-medium text-primary-600 truncate">
                           { profile.profile_name || t('greenBeanProfile.values.noName')}
                        </p>
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {t('greenBeanProfile.grade', { grade: profile.grade })}
                        </span>
                      </div>
                      <div className="mt-2 flex">
                        <div className="flex items-center text-sm text-gray-500">
                          <p className="truncate">
                            {profile.country} • {profile.region} • {profile.farm_name}
                          </p>
                        </div>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                         {profile.species} - {profile.process_type}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {profile.item_name ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-50 text-blue-700">
                            {profile.item_name}
                          </span>
                        ) : null}
                        {profile.is_default_profile ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                            {t('greenBeanProfile.values.defaultProfile')}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/purchasing/green-beans/${profile.id}${canWrite ? '/edit' : ''}`)}
                          className="p-2 text-gray-400 hover:text-primary-600"
                          title={canWrite ? t('greenBeanProfile.actions.edit') : t('common.view')}
                          data-test-id={`green-bean-edit-${profile.profile_code}`}
                        >
                          {canWrite ? <Edit className="w-5 h-5" /> : <Search className="w-5 h-5" />}
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteClick(profile.id)}
                            className="p-2 text-gray-400 hover:text-red-600"
                            title={t('greenBeanProfile.actions.delete')}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      <ConfirmationDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title={t('greenBeanProfile.deleteModal.title')}
        message={t('greenBeanProfile.deleteModal.message')}
        confirmLabel={t('greenBeanProfile.actions.delete')}
        isDestructive={true}
      />
    </div>
  );
};
